// Reusable animated avatar. Reacts to game events:
//  • correct → bounce + sparkle burst
//  • wrong   → shake + tear drop
//  • streak  → flame overlay
//  • eliminated → grayscale + skull overlay
import { motion, AnimatePresence } from "framer-motion";
import { glyphFor } from "@/lib/avatars";

/**
 * <Avatar id="fox" size={40} reaction="correct" streak={4} eliminated={false} />
 * reaction: "correct" | "wrong" | "idle"
 */
export const Avatar = ({ id = "fox", size = 40, reaction = "idle", streak = 0, eliminated = false, showRing = false, className = "" }) => {
  const g = glyphFor(id);
  const bounce = reaction === "correct";
  const shake = reaction === "wrong";
  const flame = !eliminated && streak >= 3;
  const legendary = id === "phoenix"; // creator-only mascot has a permanent aura
  return (
    <div
      className={`relative inline-block leading-none select-none ${className}`}
      style={{ width: size, height: size }}
    >
      {legendary && !eliminated && (
        <motion.span
          aria-hidden
          className="absolute inset-0 pointer-events-none rounded-full"
          style={{ boxShadow: "0 0 12px 3px rgba(251, 146, 60, 0.55), 0 0 24px 6px rgba(217, 70, 239, 0.35)" }}
          animate={{ opacity: [0.55, 1, 0.55] }}
          transition={{ duration: 1.8, repeat: Infinity }}
        />
      )}
      <motion.span
        aria-hidden
        style={{
          fontSize: size,
          filter: eliminated ? "grayscale(1) brightness(0.5)" : undefined,
          display: "inline-block",
          lineHeight: 1,
        }}
        animate={bounce
          ? { y: [0, -18, 0, -8, 0], rotate: [0, -12, 12, -6, 0], scale: [1, 1.15, 1] }
          : shake
          ? { x: [0, -6, 6, -4, 4, 0], rotate: [0, -6, 6, -3, 3, 0] }
          : { y: 0, x: 0, rotate: 0, scale: 1 }}
        transition={{ duration: bounce ? 0.7 : shake ? 0.5 : 0 }}
      >
        {g}
      </motion.span>

      {/* Sparkle burst (correct) */}
      <AnimatePresence>
        {bounce && (
          <>
            {["✨", "⭐", "💫"].map((s, i) => (
              <motion.span
                key={i}
                className="absolute pointer-events-none"
                style={{ fontSize: size * 0.5, top: 0, left: "50%" }}
                initial={{ opacity: 0, y: 0, x: -8 }}
                animate={{ opacity: [0, 1, 0], y: -size * 0.9, x: (i - 1) * size * 0.7 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.9, delay: i * 0.05 }}
              >
                {s}
              </motion.span>
            ))}
          </>
        )}
      </AnimatePresence>

      {/* Tear drop (wrong) */}
      <AnimatePresence>
        {shake && (
          <motion.span
            className="absolute pointer-events-none"
            style={{ fontSize: size * 0.45, bottom: -size * 0.1, left: "58%" }}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: [0, 1, 1, 0], y: [size * 0.1, size * 0.6] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.1 }}
          >
            💧
          </motion.span>
        )}
      </AnimatePresence>

      {/* Flame streak */}
      {flame && (
        <motion.span
          className="absolute pointer-events-none"
          style={{ fontSize: size * 0.55, top: -size * 0.25, right: -size * 0.15 }}
          animate={{ scale: [1, 1.15, 1], rotate: [0, -8, 8, 0] }}
          transition={{ duration: 0.9, repeat: Infinity }}
        >
          🔥
        </motion.span>
      )}

      {/* Skull if eliminated */}
      {eliminated && (
        <span
          className="absolute pointer-events-none"
          style={{ fontSize: size * 0.55, top: -size * 0.15, right: -size * 0.15 }}
        >
          💀
        </span>
      )}

      {/* Optional highlight ring */}
      {showRing && (
        <span
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            boxShadow: "0 0 0 3px rgba(251, 191, 36, 0.7), 0 0 12px rgba(251, 191, 36, 0.5)",
            borderRadius: "50%",
          }}
        />
      )}
    </div>
  );
};

export default Avatar;
